import React, { FC } from "react";
import { useLocale } from "../../../../contexts/localeContext";
import { TranslationKeys } from "../../../../contexts/TranslationKeys";
import { TableContextProvider } from "../../../../contexts/table/TableContextProvider";
import TableTab from "../../../tabs/table-tab";
import { NameOnlyColumn } from "../../../filter-modal/filterModalUtil";
import { ListData } from "../../../../models/ListData";
import { MENUS } from "../../../../constants/menu-types";
import { ListQuery } from "../../../../models/ListQuery";
import { useFormikContext } from "formik";
import { UI_ELEMENT_SELECT_MENU_ITEM } from "../../../../constants/widget-constants";
import { useSelector } from "react-redux";
import { IAppState } from "../../../../store/types";

interface MenuStepProps {
    onNext: () => void;
    setSelectedMenu: (menu: string) => void;
}

const MenuStep: FC<MenuStepProps> = ({ onNext, setSelectedMenu }) => {
    const { translate } = useLocale();
    const { setFieldValue, values } = useFormikContext<any>();
    const MAX_PAGE_SIZE = 8;
    const user = useSelector((state: IAppState) => (state.userInfo));
    
    const onRowClicked = (row: any) => {
        setSelectedMenu(translate.byKey(row.translationKey) ?? row.nameOriginal);
        setFieldValue('menuId', row.id);
        setFieldValue('relationships', [
            ...values.relationships.filter(relationship => relationship['eSysnameLookup'] !== UI_ELEMENT_SELECT_MENU_ITEM),
            {
                eSysnameLookup: UI_ELEMENT_SELECT_MENU_ITEM,
                menuId: row.id
            }
        ]);
    }

    const getMenus = (listParameters?: ListQuery): Promise<ListData> => {
        const startIndex = listParameters?.goToPage && listParameters.goToPage > 1 ? (listParameters.goToPage - 1) * MAX_PAGE_SIZE : 0;
        const endIndex = listParameters?.goToPage && listParameters.goToPage > 1 ? listParameters.goToPage * MAX_PAGE_SIZE : MAX_PAGE_SIZE;

        return new Promise((resolve, reject) => {
            const userMenus = MENUS.map((menu) => {
                const pathItems = menu.path.split('/');
                const route = pathItems[pathItems.length - 1];

                // Check if the user has permission for the route
                if (user.permissions.includes("show." + route + ".element")) {
                    return {
                        ...menu,
                        name: translate.byKey(menu.translationKey) ?? menu.name
                    };
                }
                return null; // Return null if the permission is not present
            })
            .filter(menu => menu !== null); // Filter out null values

            return resolve({
                rows: userMenus.slice(startIndex, endIndex),
                pagination: { maxPageSize: MAX_PAGE_SIZE, pageNumber: listParameters?.goToPage ?? 1, totalCount: userMenus.length, totalPages: Math.ceil(userMenus.length / MAX_PAGE_SIZE) }
            });
        })
    }

    const getMenuId = () : string => {
        const menu = values.relationships.find((relationship: any) => relationship.eSysnameLookup === UI_ELEMENT_SELECT_MENU_ITEM);

        if (menu) {
            return menu.menuId;
        }

        return "";
    }

    return <div className="pt-3 pr-3 h-full relative">
        <TableContextProvider
            useTable
            dataMethod={getMenus}
            tableDescriptor={NameOnlyColumn}
            searchDescriptor={[]}
            disableSearch
            useSearch
            canExportReport={false}>
            <TableTab
                title={translate.byKey(TranslationKeys.selectMenu)}
                selectRows={false}
                onRowClicked={onRowClicked}
                isPaginationSupported={true}
                customHighlightRow={{ fieldName: "id", comparisonValue: getMenuId() }}
                cyName="menuStep">
            </TableTab>
        </TableContextProvider>

        <div className="flex justify-end w-full absolute bottom-0 pr-3 pb-3 z-50 h-16 bg-white">
            <button
                className={`py-1 px-5 mt-3 secondary text-white ${values.menuId ? `bg-portal-blue-primary-shade2` : `secondary-disabled cursor-not-allowed`}`}
                type="button"
                disabled={values.menuId ? false : true}
                onClick={onNext}>{translate.byKey(TranslationKeys.next)}</button>
        </div>
    </div>
}

export default MenuStep;